import React, { useState, useMemo } from 'react';
import classNames from 'classnames';
import styled from 'styled-components';

import { useDnDSortableItem } from '../../../hooks';
import { FileDropContainer } from './FileDropContainer';
import { DataStatusHandler } from './DataStatusHandler';
import { DraggableDnDContainer } from './DraggableDnDContainer';
import { FolderManagerActionTypes, FolderManagerItemTypes } from '../../../interfaces';

//region INTERFACES
//@fixme: fix all types
interface Column {
  label: string;
  field: string;
  width?: string;
  customRender?: (row: any) => React.ReactNode;
  sort?: (a: any, b: any) => number;
}

interface SortState {
  field: string | null;
  direction: 'asc' | 'desc';
}

interface RenderTableHeader {
  columns: Column[];
  sortState: SortState;
  onSort: (field: string) => void;
}

type OnSelect = (data: { event: any; item: any }) => void;

interface FolderManagerTableViewProps {
  items: any[];
  noDataMessage?: string;
  columns: Column[];
  selectedRows: any[];
  onDragEnd?: any;
  onDoubleClick?: any;
  onSelect?: any;
  onUpload?: any;
  minHeight?: string;
  onDragStart?: any;
  isLoading: boolean;
  onAction: (action: IOnAction<any>) => void;
  storageKey?: string; // Key for localStorage persistence

  disableUpload?: boolean;
  disableInternalDnD?: boolean;
  acceptedDragTypes?: string[];
}

interface TableViewProps {
  rows: any[];
  noDataMessage?: string;
  columns: Column[];
  overId?: number;
  isLoading: boolean;
  selectedRows: any[];
  disableUpload?: boolean;
  acceptedDragTypes?: string[];
  onSelect: () => void;
  onDoubleClick: () => void;
  onAction: (action: IOnAction<any>) => void;
  storageKey?: string;
}

interface RenderTableBodyProps {
  columns: Column[];
  rows: any[];
  noDataMessage?: string;
  selectedRows: any[];
  isLoading: boolean;
  acceptedDragTypes?: string[];
  onSelect: OnSelect;
  onAction: (action: IOnAction<any>) => void;
  onDoubleClick: (data: { row: number; index: number }) => void;
}

interface DraggableTableRowProps {
  columns: Column[];
  item: any;
  onSelect: OnSelect;
  selectedRows: any[];
  acceptedDragTypes?: string[];
  onDoubleClick: (data: any) => void;
  onAction: (action: IOnAction<any>) => void;
}

//endregion

//region STYLED
const StyledTable = styled.table`
  width: 100%;
  height: 100%;
  //min-height: 60vh;
  margin: 0;
  padding: 0;
  border-collapse: collapse;
  box-sizing: border-box;
  position: relative;
  background-color: white;
  border: 1px solid rgb(224, 224, 224);
  line-height: 1.43;
  letter-spacing: 0.01071em;
  outline: none;
`;

const StyledHeaderRow = styled.tr`
  font-weight: 500;
  font-size: ${({ theme }) => theme.fontSize.default};
  line-height: 56px;
  color: rgba(63, 63, 63, 0.87); //@FIXME: get from theme?
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  border-bottom: 1px solid rgb(224, 224, 224);
`;

const StyledHeaderCell = styled.th<{ width: string; $sortable?: boolean }>`
  text-align: left;
  width: ${({ width }) => width};
  padding: 0 10px;
  cursor: ${({ $sortable }) => ($sortable ? 'pointer' : 'default')};
  user-select: none;
  position: relative;

  &:hover {
    background-color: ${({ $sortable }) => ($sortable ? 'rgba(0, 0, 0, 0.04)' : 'transparent')};
  }
`;

const StyledHeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StyledSortIcon = styled.span<{ $direction?: 'asc' | 'desc' | null }>`
  display: inline-flex;
  flex-direction: column;
  margin-left: 4px;
  opacity: ${({ $direction }) => ($direction ? 1 : 0.3)};

  &::before {
    content: '▲';
    font-size: 8px;
    line-height: 1;
    color: ${({ $direction }) => ($direction === 'asc' ? '#1976d2' : '#ccc')};
  }

  &::after {
    content: '▼';
    font-size: 8px;
    line-height: 1;
    color: ${({ $direction }) => ($direction === 'desc' ? '#1976d2' : '#ccc')};
  }
`;

const StyledRow = styled.tr<{ $isNotAccepted?: boolean; $alreadyAdded?: boolean }>`
  font-weight: 400;
  font-size: ${({ theme }) => theme.fontSize.default};
  line-height: 32px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  transition: all 0.3s ease-in;
  user-select: none;

  ${({ $isNotAccepted }) =>
    $isNotAccepted &&
    `
    display: none;
    opacity: 0.5;
    cursor: not-allowed !important;
  `}

  ${({ $alreadyAdded }) =>
    $alreadyAdded &&
    `
    cursor: default;

    td:not(.action-cell) {
      opacity: 0.4;
    }
  `}

  .show-on-hover {
    display: none;
  }

  &.over {
    border: 2px solid blue;
  }

  &:hover {
    cursor: ${({ $isNotAccepted }) => ($isNotAccepted ? 'not-allowed' : 'pointer')};
    background-color: ${({ theme, $isNotAccepted }) =>
      !$isNotAccepted ? theme.palette.hoverBackgroundColor : 'transparent'};
    transition: all 0.3s ease-out;

    .show-on-hover {
      display: ${({ $isNotAccepted }) => ($isNotAccepted ? 'none' : 'inline-block')};
    }
  }

  &.selected {
    background-color: ${({ theme }) => theme.palette.selectedBackgroundColor};
  }
`;

const StyledRowCell = styled.td`
  width: ${({ width }) => width};
  padding: 0 10px;
  vertical-align: top;
`;

const StyledTableContainer = styled.div`
  width: 100%;
  overflow: auto;
`;
//endregion

//region HELPERS
// localStorage utilities
const getSortStateFromStorage = (storageKey: string): SortState => {
  try {
    const stored = localStorage.getItem(`@FolderManagerTableView/${storageKey}`);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.warn('Failed to parse sort state from localStorage:', error);
  }
  return { field: null, direction: 'asc' };
};

const saveSortStateToStorage = (storageKey: string, sortState: SortState): void => {
  try {
    localStorage.setItem(`@FolderManagerTableView/${storageKey}`, JSON.stringify(sortState));
  } catch (error) {
    console.warn('Failed to save sort state to localStorage:', error);
  }
};

const renderTableHeader = ({ columns, sortState, onSort }: RenderTableHeader) => {
  return (
    <thead>
      <StyledHeaderRow>
        {(columns || []).map((column, index) => {
          const isSortable = !!column.sort;
          const isCurrentSort = sortState.field === column.field;
          const sortDirection = isCurrentSort ? sortState.direction : null;

          return (
            <StyledHeaderCell
              key={index}
              width={columns?.[index]?.width || 'auto'}
              $sortable={isSortable}
              onClick={() => isSortable && onSort(column.field)}
            >
              <StyledHeaderContent>
                <span>{column?.label}</span>
                {isSortable && <StyledSortIcon $direction={sortDirection} />}
              </StyledHeaderContent>
            </StyledHeaderCell>
          );
        })}
      </StyledHeaderRow>
    </thead>
  );
};

const renderTableBody = ({
  columns,
  rows,
  selectedRows,
  isLoading,
  onSelect,
  onAction,
  noDataMessage,
  onDoubleClick,
  acceptedDragTypes,
}: RenderTableBodyProps) => {
  return (
    <tbody style={{ position: 'relative' }}>
      {!rows.length || isLoading ? (
        <tr>
          <td colSpan={columns.length} style={{ height: '30vh' }}>
            <DataStatusHandler
              columns={columns}
              message={noDataMessage}
              showNoDataMessage={!rows.length}
              isLoading={isLoading}
              showLoader
              isTable
            />
          </td>
        </tr>
      ) : (
        (rows || []).map((row, index) => (
          <DraggableTableRow
            key={`${row.type}-${row.id}`}
            item={row}
            columns={columns}
            selectedRows={selectedRows}
            acceptedDragTypes={acceptedDragTypes}
            onAction={onAction}
            onSelect={onSelect}
            onDoubleClick={() => onDoubleClick({ row, index })}
          />
        ))
      )}
    </tbody>
  );
};
//endregion

//region COMPONENTS
const DraggableTableRow = ({
  columns,
  item,
  onSelect,
  onAction,
  selectedRows,
  acceptedDragTypes,
  onDoubleClick,
}: DraggableTableRowProps) => {
  const isFolder = item.type === FolderManagerItemTypes.FOLDER || item.type === FolderManagerItemTypes.WORKSPACE;
  const isTypeAccepted = !acceptedDragTypes || isFolder || acceptedDragTypes.includes(item.type);
  const isAlreadyAdded = item.selectable === false;

  const shouldDisableDnD = !isTypeAccepted || isAlreadyAdded;

  const dndHooks = useDnDSortableItem({
    item: item,
    // overId: +overId,
    overId: null,
    selectedRows: selectedRows,
  });

  const manualIsSelected = shouldDisableDnD
    ? (selectedRows || []).some((row: any) => {
        if ((item as any).instanceId && (row as any).instanceId) {
          return (item as any).instanceId === (row as any).instanceId;
        }

        return row.id === item.id && row.type === item.type;
      })
    : false;

  //@ts-ignore
  const { isOver, isSelected, listeners, attributes, setNodeRef, itemStyle } = shouldDisableDnD
    ? {
        isOver: false,
        isSelected: manualIsSelected,
        listeners: {},
        attributes: {},
        setNodeRef: null,
        itemStyle: {},
      }
    : dndHooks;

  const isUploadDisabled = item?.type === FolderManagerItemTypes.FOLDER;

  const handleSelect = (event: any) => {
    if (isAlreadyAdded) {
      return;
    }
    // prevent text selection only when shift key is pressed
    if (event?.shiftKey) {
      event.preventDefault();
    }
    onSelect({ event, item });
  };

  return (
    <FileDropContainer
      isItemContainer
      isDisabled={isUploadDisabled}
      onUpload={(fileList) =>
        onAction({
          actionType: FolderManagerActionTypes.UPLOAD,
          payload: { files: fileList, item: item },
        })
      }
      renderAsItem
    >
      <StyledRow
        ref={shouldDisableDnD ? undefined : setNodeRef}
        style={shouldDisableDnD ? {} : itemStyle}
        $isNotAccepted={!isTypeAccepted && !isFolder}
        $alreadyAdded={isAlreadyAdded}
        {...(shouldDisableDnD ? {} : attributes)}
        {...(shouldDisableDnD ? {} : listeners)}
        onClick={handleSelect}
        onDoubleClick={onDoubleClick}
        className={classNames({ over: isOver, selected: isSelected })}
      >
        {columns.map((column, columnIndex) => (
          <StyledRowCell
            key={columnIndex}
            width={columns?.[columnIndex]?.width || 'auto'}
            className={classNames({ 'action-cell': column.field === 'action' })}
          >
            {column?.customRender && typeof column?.customRender === 'function'
              ? column.customRender(item)
              : item?.[column?.field]}
          </StyledRowCell>
        ))}
      </StyledRow>
    </FileDropContainer>
  );
};

const TableView = ({
  rows,
  columns,
  isLoading,
  noDataMessage,
  onSelect,
  onAction,
  onDoubleClick,
  selectedRows,
  storageKey,
  acceptedDragTypes,
}: TableViewProps) => {
  // Initialize sort state from localStorage if storageKey is provided
  const [sortState, setSortState] = useState<SortState>(() => {
    if (storageKey) {
      return getSortStateFromStorage(storageKey);
    }
    return { field: null, direction: 'asc' };
  });

  const handleSort = (field: string) => {
    setSortState((prevState) => {
      let newState: SortState;

      if (prevState.field === field) {
        // Three-click cycle: asc -> desc -> null (disabled)
        if (prevState.direction === 'asc') {
          newState = { field, direction: 'desc' };
        } else if (prevState.direction === 'desc') {
          newState = { field: null, direction: 'asc' }; // Disable sorting
        } else {
          newState = { field, direction: 'asc' };
        }
      } else {
        // New field, start with ascending
        newState = { field, direction: 'asc' };
      }

      // Save to localStorage if storageKey is provided
      if (storageKey) {
        saveSortStateToStorage(storageKey, newState);
      }

      return newState;
    });
  };

  const sortedRows = useMemo(() => {
    if (!sortState.field) {
      return rows;
    }

    const column = columns.find((col) => col.field === sortState.field);
    if (!column?.sort) {
      return rows;
    }

    const sorted = [...rows].sort(column.sort);
    return sortState.direction === 'desc' ? sorted.reverse() : sorted;
  }, [rows, sortState, columns]);

  return (
    <StyledTableContainer>
      <StyledTable>
        {renderTableHeader({ columns, sortState, onSort: handleSort })}
        {renderTableBody({
          noDataMessage,
          columns,
          rows: sortedRows,
          selectedRows,
          isLoading,
          onSelect,
          onAction,
          onDoubleClick,
          acceptedDragTypes,
        })}
      </StyledTable>
    </StyledTableContainer>
  );
};
//endregion

export const FolderManagerTableView = ({
  items,
  columns,
  noDataMessage,
  selectedRows,
  onDragEnd,
  onDoubleClick,
  minHeight,
  onSelect,
  onAction,
  onDragStart,
  isLoading,
  storageKey,
  disableUpload = false,
  disableInternalDnD = false,
  acceptedDragTypes,
}: FolderManagerTableViewProps) => {
  return (
    <FileDropContainer
      isDisabled={disableUpload}
      minHeight={minHeight}
      onUpload={(fileList) =>
        onAction({
          actionType: FolderManagerActionTypes.UPLOAD,
          payload: { files: fileList, item: null },
        })
      }
    >
      {disableInternalDnD ? (
        <TableView
          columns={columns}
          noDataMessage={noDataMessage}
          rows={items}
          onDoubleClick={onDoubleClick}
          isLoading={isLoading}
          onAction={onAction}
          onSelect={onSelect}
          selectedRows={selectedRows}
          storageKey={storageKey}
          disableUpload={disableUpload}
          acceptedDragTypes={acceptedDragTypes}
        />
      ) : (
        <DraggableDnDContainer onDragEnd={onDragEnd} onDragStart={onDragStart} items={items}>
          <TableView
            columns={columns}
            noDataMessage={noDataMessage}
            rows={items}
            onDoubleClick={onDoubleClick}
            isLoading={isLoading}
            onAction={onAction}
            onSelect={onSelect}
            selectedRows={selectedRows}
            storageKey={storageKey}
            disableUpload={disableUpload}
            acceptedDragTypes={acceptedDragTypes}
          />
        </DraggableDnDContainer>
      )}
    </FileDropContainer>
  );
};
